// Split out of EnhancedEvaluationDisplay.tsx (react-refresh/only-export-components)
// — plain pure helpers, not components. Behavior unchanged.
//
// Template trim-window helpers (TVT-2, template-video trimming)

/** Convert a template trim window from FULL-video frame space + fps to seconds.
 *
 *  `endFrame` is INCLUSIVE (the last scored frame), matching the backend
 *  `_apply_trim_boundaries` slice `[start : end + 1]`, so we add 1 frame worth of
 *  time to `endSec` to keep the last frame fully visible.  A full-video template
 *  (endFrame null/undefined) yields `endSec === null` and, with startFrame 0,
 *  `startSec === 0` → the display treats it as "no clamp". */
export function templateWindowSecondsFromFrames(
  startFrame: number | undefined | null,
  endFrame: number | undefined | null,
  fps: number | undefined | null,
): { startSec: number; endSec: number | null } {
  const safeFps = fps != null && fps > 0 ? fps : 30;
  const s = startFrame != null && startFrame > 0 ? startFrame : 0;
  return {
    startSec: s / safeFps,
    endSec: endFrame != null ? (endFrame + 1) / safeFps : null,
  };
}

/** Decide where template playback should jump to keep it inside [startSec, endSec).
 *
 *  Returns the time to seek to, or `null` when the current time is already within
 *  the window (no change needed).  Past the end → loop to `startSec`; before the
 *  start → snap to `startSec`.  `endSec === null` means "to end of clip" (no upper
 *  bound).  For a full-video window (startSec 0, endSec null) this always returns
 *  `null` → playback is never altered. */
export function nextTemplateWindowTime(
  currentTime: number,
  startSec: number,
  endSec: number | null,
  eps = 0.02,
): number | null {
  if (endSec !== null && currentTime >= endSec - eps) return startSec;
  if (currentTime < startSec - eps) return startSec;
  return null;
}
